import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apv as api, productivite as apiProd } from '../services/api';
import { useFiltresVentes, usePanneau, basculer } from './ventesUtils';
import AvancementMois from './AvancementMois';
import { SelecteurMaille, useRegroupement, construireArbre, axesDisponibles, sectionsGroupes, basculerGroupe } from './regroupements';
import FiltresPanel from './FiltresPanel';
import './Ventes.css';  // gabarit commun : vt-page, vt-kpis, vt-card (CLAUDE.md §1 et §3)
import './Stock.css';   // st-tools / st-seg du réglage d'affichage
import './VentesJour.css';  // rg-* du regroupement, partagé avec le rapport VN
import './ApvJour.css'; // compléments propres à ce rapport

// ─── APV — Le mois au jour le jour ───────────────────────────────────────────
// Outil du chef d'atelier : où en est son mois, et ce qu'il a fait chaque jour.
//
// ⚠ UNE LIGNE PAR JOUR, pas une colonne. 22 à 31 colonnes ne tiennent sur aucun
// écran — l'Analyse croisée s'en sort avec 12 mois, pas avec un mois de jours.
// Et le jour est l'ENTITÉ, les indicateurs sont ses attributs : lire une
// journée, c'est lire une ligne, pas balayer une colonne à la verticale.
//
// ⚠ MONTANTS EN EUROS EXACTS. À l'échelle d'une journée, l'arrondi au millier
// efface l'information (11 k€ pour 11 402 € comme pour 11 498 €) et interdit
// tout rapprochement avec une facture.
//
// ⚠ Le bandeau est un CUMUL DU MOIS, pas la veille. Ce qu'un chef d'atelier
// regarde en arrivant, c'est l'avancement de SON mois ; le chiffre d'hier se lit
// dans la dernière ligne du tableau, surlignée.

const nf = new Intl.NumberFormat('fr-FR');
const eur = (v) => (v == null ? '—' : `${nf.format(Math.round(v))} €`);
const nb = (v) => (v == null ? '—' : nf.format(Math.round(v)));
const pct0 = (v) => `${Math.round(v)} %`;
const heures = (v) => (v == null ? '—' : `${nf.format(Math.round(v))} h`);
const pctNul = (v) => (v == null ? '—' : pct0(v));

const jourCourt = (s) => {
  const d = new Date(`${s}T00:00:00`);
  return `${d.toLocaleDateString('fr-FR', { weekday: 'long' })} ${d.getDate()}`;
};
// Un samedi ou un dimanche facturé n'est pas une anomalie de données : ce sont
// des avoirs ou des urgences. Les masquer serait mentir ; les laisser muets au
// milieu des jours ouvrés fait douter du tableau. On les marque.
const estWeekend = (s) => [0, 6].includes(new Date(`${s}T00:00:00`).getDay());
const moisLong = (ym) => {
  const [a, m] = ym.split('-');
  return new Date(Number(a), Number(m) - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};
const moisPrecedents = (n) => {
  const out = [];
  const d = new Date(); d.setDate(1);
  for (let i = 0; i < n; i++) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
};

// Le sélecteur de mois prend la place de la « Période » du panneau : un seul
// mois à la fois, d'où des boutons radio et non des cases cumulables.
// ⚠ Un rapport quotidien se lit sur le mois en cours, et au plus sur le
// précédent. Les treize mois en radios occupaient la moitié du panneau : les
// plus anciens passent en liste déroulante, sans rien perdre.
// Les axes du bloc de répartition. Les quatre portent le CA COMPLET (M.O. +
// pièces) : le type (Payant / Garantie / Cession) vient de la main d'œuvre et
// se propage par le numéro d'OR. Contrôlé : les quatre totalisent le CA du mois
// à l'euro d'arrondi près.
const AXES_APV = [
  { c: 'marque', lib: 'marque', col: 'Marque' },
  { c: 'atelier', lib: 'atelier', col: 'Atelier' },
  { c: 'type', lib: 'type', col: 'Type' },
  { c: 'receptionnaire', lib: 'réceptionnaire', col: 'Réceptionnaire' },
];

const MOIS_RECENTS = moisPrecedents(2);
const MOIS_CHOIX = [
  { code: '', label: 'Mois en cours' },
  { code: MOIS_RECENTS[1], label: moisLong(MOIS_RECENTS[1]) },
];
const MOIS_AUTRES = moisPrecedents(13).slice(2).map((m) => ({ code: m, label: moisLong(m) }));

// Trois groupes plutôt que huit colonnes de même poids : l'œil lit d'abord des
// blocs (ce que j'ai vendu, ce que j'ai gagné, comment j'ai travaillé), et
// seulement ensuite le détail. Les colonnes « dont » sont subordonnées à leur
// total, et discrètes.
// Vue d'ensemble pour la direction : les memes indicateurs que le tableau
// quotidien, par concession, en cumul du mois. Memes definitions, sinon les
// totaux ne se recoupent pas.
const SOC = [
  { c: 'ca', lib: 'CA atelier HT', libSansPieces: 'CA M.O. HT', f: (s) => s.ca, fmt: (v) => eur(v) },
  { c: 'mo', lib: 'dont M.O.', f: (s) => s.mo, fmt: (v) => eur(v), discret: true, si: 'pieces' },
  // CA/jour = cumul du mois ÷ jours ouvrés écoulés de l'île de la concession
  // (calculé par le serveur) ; un regroupement ou l'Ensemble ADDITIONNE ceux
  // de ses concessions (2026-09-25).
  { c: 'moJour', lib: 'CA/jour M.O.', f: (s) => s.moJour, fmt: (v) => eur(v), discret: true, si: 'pieces' },
  { c: 'pieces', lib: 'dont pièces', f: (s) => s.pieces, fmt: (v) => eur(v), discret: true, si: 'pieces' },
  { c: 'piecesJour', lib: 'CA/jour pièces', f: (s) => s.piecesJour, fmt: (v) => eur(v), discret: true, si: 'pieces' },
  // Sous-traitance (travaux extérieurs, 2026-11-03) : sortie des pièces, sans
  // heures — elle n'entre donc pas dans le taux horaire.
  { c: 'st', lib: 'dont sous-trait.', f: (s) => s.st, fmt: (v) => eur(v), discret: true, si: 'pieces' },
  { c: 'marge', lib: 'Marge', f: (s) => s.marge, fmt: (v) => eur(v) },
  { c: 'tx', lib: 'Taux', f: (s) => (s.ca ? (s.marge / s.ca) * 100 : null), fmt: (v) => (v == null ? '\u2014' : pct0(v)), discret: true },
  { c: 'ors', lib: 'OR', f: (s) => s.ors, fmt: (v) => nb(v) },
  { c: 'ticket', lib: 'Ticket moyen', f: (s) => (s.ors ? s.ca / s.ors : null), fmt: (v) => (v == null ? '\u2014' : eur(v)), discret: true },
  { c: 'th', lib: 'Taux horaire', f: (s) => (s.heures ? s.mo / s.heures : null), fmt: (v) => (v == null ? '\u2014' : eur(v)), discret: true },
];

const IND = [
  { c: 'ca', g: 'Chiffre d\'affaires', lib: 'CA atelier HT', libSansPieces: 'CA M.O. HT', sous: 'M.O. + pièces + sous-trait.', sousSansPieces: null, f: (j) => j.ca, fmt: eur, barre: true, fort: true },
  { c: 'mo', g: 'Chiffre d\'affaires', lib: 'dont M.O.', f: (j) => j.mo, fmt: eur, discret: true, si: 'pieces' },
  { c: 'pieces', g: 'Chiffre d\'affaires', lib: 'dont pièces', f: (j) => j.pieces, fmt: eur, discret: true, si: 'pieces' },
  { c: 'st', g: 'Chiffre d\'affaires', lib: 'dont sous-trait.', sous: 'trav. ext.', f: (j) => j.st, fmt: eur, discret: true, si: 'pieces' },
  { c: 'marge', g: 'Rentabilité', lib: 'Marge', sous: 'sur prix d\'achat', f: (j) => j.marge, fmt: eur },
  { c: 'tx', g: 'Rentabilité', lib: 'Taux', f: (j) => (j.ca ? (j.marge / j.ca) * 100 : null), fmt: pct0, ratio: true, discret: true },
  { c: 'ors', g: 'Activité', lib: 'OR', sous: 'facturés', f: (j) => j.ors, fmt: nb },
  { c: 'ticket', g: 'Activité', lib: 'Ticket moyen', sous: 'CA ÷ OR', f: (j) => (j.ors ? j.ca / j.ors : null), fmt: eur, ratio: true, discret: true },
  { c: 'th', g: 'Activité', lib: 'Taux horaire', sous: 'M.O. ÷ heures', f: (j) => (j.heures ? j.mo / j.heures : null), fmt: eur, ratio: true, discret: true },
];
// Une ligne d'en-tête par groupe : [{ lib, n }] avec n = nombre de colonnes.
const groupes = (cols) => cols.reduce((acc, c) => {
  const d = acc[acc.length - 1];
  if (d && d.lib === (c.g || '')) d.n += 1; else acc.push({ lib: c.g || '', n: 1 });
  return acc;
}, []);

// ⚠ AUCUNE BARRE DE COULEUR DANS UNE CARTE (convention du 2026-09-18, voir
// CLAUDE.md) : un tableau se lit en chiffres, et une barre mise à côté du
// nombre qu'elle représente n'ajoute rien qu'on ne lise déjà. Le CA atelier est
// simplement en gras ; le poids relatif d'un jour se voit dans les chiffres.
// Les couleurs restent aux graphiques, qui en ont besoin.
// La vue « par atelier » a perdu sa teinte d'intensité le 2026-10-24 (demande
// du user) : elle se lit en chiffres, comme les deux autres vues.

// Format commun a tous les rapports : `vt-kpis` / `vt-kpi`, trois niveaux
// (.l libelle, .v valeur, .d variation). Pas de carte « heros » a double
// largeur : un indicateur qui domine est le PREMIER de la grille, pas le plus
// gros. Voir CLAUDE.md, section 3.
function Kpi({ lab, val, sub }) {
  return (
    <div className="vt-kpi">
      <div className="l">{lab}</div>
      <div className="v">{val}</div>
      <div className="d aj-d">{sub}</div>
    </div>
  );
}

// Détail des factures (2026-10-27) : filtré au clic sur les blocs du haut —
// un jour du tableau quotidien, une ligne de « Le mois par … ». Même geste que
// « Stock par marque » ▸ détail des véhicules : plusieurs lignes d'un même bloc
// se cumulent (« FORD ou PEUGEOT »), les blocs se croisent, recliquer retire.
// Ces filtres ne s'appliquent qu'au détail ; le filtre société (bloc par
// concession, panneau) s'applique, lui, à toute la page. Un seul état : l'URL.
const FILTRES_FACT = [
  { c: 'jour', url: 'f_jours', lib: 'Jour' },
  { c: 'marque', url: 'f_marques', lib: 'Marque' },
  { c: 'atelier', url: 'f_ateliers', lib: 'Atelier' },
  { c: 'type', url: 'f_types', lib: 'Type' },
  { c: 'receptionnaire', url: 'f_receptionnaires', lib: 'Réceptionnaire' },
];
const MAX_FACTURES = 300;
const eur2 = (v) => (v == null ? '—' : `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)} €`);

export default function ApvJour({ branche = 'auto-antilles' }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [vue, setVue] = useState('ind');
  const [dispo, setDispo] = useState([]);
  // Les filtres vivent dans le PANNEAU DE DROITE, comme sur tous les rapports.
  // Le mois y prend la place de la période : l'unité de cet écran EST le mois,
  // et `FiltresPanel` sait déjà rendre une liste de choix uniques (`periodes`).
  // Mois et sociétés restent portés par l'URL, comme ailleurs.
  const f = useFiltresVentes(dispo);
  const [ouvert, ouvrirPanneau] = usePanneau();
  // Axe du bloc de répartition — réglage d'affichage, donc en en-tête de carte.
  const [axe, setAxe] = useState('marque');
  // Cliquer une concession regle le FILTRE DU PANNEAU, seul etat de filtrage de
  // la page. Recliquer la seule concession retenue revient a tout le perimetre.
  // Le périmètre métropole ne porte pas les pièces : les colonnes « dont M.O. »
  // et « dont pièces » n'auraient plus de sens (le CA EST la M.O.), et le bloc
  // d'objectif non plus. Ils disparaissent au lieu d'afficher des zéros.
  const avecPieces = data?.avecPieces !== false;
  const garde = (cols) => cols
    .filter((c) => !c.si || (c.si === 'pieces' ? avecPieces : true))
    .map((c) => (avecPieces || !c.libSansPieces ? c
      : { ...c, lib: c.libSansPieces, sous: c.sousSansPieces }));
  const SOC_V = useMemo(() => garde(SOC), [avecPieces]);   // eslint-disable-line react-hooks/exhaustive-deps
  const IND_V = useMemo(() => garde(IND), [avecPieces]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Maille d'affichage du bloc par concession : concession, site, plaque.
  const maille = useRegroupement();
  const feuilles = useMemo(
    () => (data?.societesCumul || []).map((s) => ({ ...s, libelle: s.societe, code: s.societeCode })),
    [data]);
  const axes = useMemo(() => axesDisponibles(feuilles), [feuilles]);
  // ⚠ Seules les mesures ADDITIVES se somment ; les taux (marge, ticket moyen,
  // taux horaire) sont recalculés par `SOC` à partir de ces sommes. Moyenner
  // des taux donnerait un chiffre faux dès que deux concessions n'ont pas le
  // même volume.
  // Le CA/jour d'un regroupement est la somme de ceux de ses membres.
  const plus = (a, b) => (b == null ? a : (a || 0) + b);
  const sommerConcessions = (membres) => membres.reduce((t, m) => ({
    ca: t.ca + m.ca, mo: t.mo + m.mo, pieces: t.pieces + m.pieces, st: t.st + (m.st || 0),
    marge: t.marge + m.marge, ors: t.ors + m.ors, heures: t.heures + m.heures,
    moJour: plus(t.moJour, m.moJour), piecesJour: plus(t.piecesJour, m.piecesJour),
  }), { ca: 0, mo: 0, pieces: 0, st: 0, marge: 0, ors: 0, heures: 0, moJour: null, piecesJour: null });
  const AXE_APV = AXES_APV.find((a) => a.c === axe) || AXES_APV[0];
  // ⚠ La répartition ne porte PAS les heures : elles vivent dans `apv_mo`, qui
  // ne couvre que la main d'œuvre, et les rapprocher par OR fausserait le CA.
  // La colonne « Taux horaire » serait donc vide sur chaque ligne — on la
  // retire plutôt que d'aligner des tirets.
  const SOC_AXE = useMemo(() => SOC_V.filter((c) => c.c !== 'th'), [SOC_V]);

  const arbreConcessions = useMemo(
    () => construireArbre(feuilles, maille.niveau, sommerConcessions, maille.ouverts, (x) => x.ca),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feuilles, maille.niveau, maille.ouverts]);

  const societes = f.societesUrl;
  const mois = f.moisStr;
  const setMois = (m) => f.setFiltre({ mois: m });

  const charger = useCallback(async () => {
    setErreur('');
    try {
      const { data: d } = await api.jour({ branche, mois, societes });
      setData(d);
      setDispo(d.societesDispo || []);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Chargement impossible');
      setData(null);
    }
  }, [branche, mois, societes]);
  useEffect(() => { charger(); }, [charger]);
  // Factures du mois : chargées à part, après le reste de la page.
  const [params, setParams] = useSearchParams();
  const [factures, setFactures] = useState(null);
  useEffect(() => {
    let actif = true; setFactures(null);
    api.factures({ branche, mois: mois || undefined, societes: societes || undefined })
      .then((r) => { if (actif) setFactures(r.data.factures || []); })
      .catch(() => { if (actif) setFactures({ erreur: true }); });
    return () => { actif = false; };
  }, [branche, mois, societes]);
  const filtresFact = useMemo(() => Object.fromEntries(FILTRES_FACT.map((x) => [x.c, (params.get(x.url) || '').split('|').filter(Boolean)])), [params]);
  const setFiltresFact = (fx) => {
    const next = new URLSearchParams(params);
    FILTRES_FACT.forEach((x) => { const l = fx[x.c] || []; if (l.length) next.set(x.url, l.join('|')); else next.delete(x.url); });
    setParams(next, { replace: true });
  };
  const basculerFact = (c, v) => setFiltresFact({ ...filtresFact, [c]: filtresFact[c].includes(v) ? filtresFact[c].filter((x) => x !== v) : [...filtresFact[c], v] });
  const nbFiltresFact = FILTRES_FACT.reduce((n, x) => n + filtresFact[x.c].length, 0);
  const libFiltreFact = (c, v) => (c === 'jour' ? new Date(`${v}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }) : v);
  const pucesFact = FILTRES_FACT.flatMap((x) => filtresFact[x.c].map((v) => ({ c: x.c, lib: x.lib, v })));
  const facturesFiltrees = useMemo(() => (Array.isArray(factures) ? factures.filter((fa) => FILTRES_FACT.every((x) => !filtresFact[x.c].length || filtresFact[x.c].includes(fa[x.c]))) : []), [factures, filtresFact]);
  const [triFact, setTriFact] = useState({ c: 'jour', desc: true });
  const facturesTriees = useMemo(() => [...facturesFiltrees].sort((a, b) => {
    const x = a[triFact.c]; const y = b[triFact.c];
    const c = typeof x === 'number' ? x - y : String(x).localeCompare(String(y), 'fr');
    return (triFact.desc ? -c : c) || b.ca - a.ca;
  }), [facturesFiltrees, triFact]);
  // Onglet « Productivité » (2026-10-24) : pointages compagnons du mois, jour
  // par jour (APV — Productivité, Auto Antilles seulement). Chargé à la
  // première ouverture de l'onglet, puis à chaque changement de filtre.
  const prodDispo = branche === 'auto-antilles';
  const [prod, setProd] = useState(null);
  useEffect(() => {
    if (vue !== 'prod' || !prodDispo) return undefined;
    let actif = true;
    setProd(null);
    apiProd.charger({ mois: mois || undefined, societes: societes || undefined })
      .then((r) => { if (actif) setProd(r.data); })
      .catch(() => { if (actif) setProd({ erreur: true, parJour: [] }); });
    return () => { actif = false; };
  }, [vue, prodDispo, mois, societes]);

  const J = useMemo(() => data?.jours || [], [data]);
  // Jours ouvrés sans facture (2026-10-20) : présents à zéro dans le tableau,
  // ils ne comptent pas parmi les « jours facturés ».
  const N = J.filter((j) => !j.sansFacture).length;
  const iDernier = J.reduce((d, j, i) => (j.sansFacture ? d : i), -1);
  // Moyenne par jour = total ÷ jours OUVRÉS écoulés (un jour ouvré sans facture
  // compte, un samedi facturé non) : même base que le CA/jour du bloc du haut.
  const joursOuvresEcoules = J.filter((j) => !estWeekend(j.jour)).length;

  // Avancement : le réalisé sur l'objectif, comparé au rythme HABITUEL et non à
  // la part des jours écoulés. Le mois se termine plus fort qu'il ne commence.
  const avanc = (v, o) => {
    if (!o) return null;
    const p = (v / o) * 100;
    const e = p - data.rythme.attendu;
    return { p, e, cl: Math.abs(e) < 2 ? 'aj-flat' : e > 0 ? 'aj-up' : 'aj-down' };
  };
  const Avanc = ({ v, o }) => {
    const a = avanc(v, o);
    if (!a) return <span className="aj-faint">pas d'objectif</span>;
    return <><b>{pct0(a.p)}</b> de l'objectif · <span className={a.cl}>
      {Math.abs(a.e) < 2 ? '≈' : a.e > 0 ? '▲' : '▼'} {Math.abs(Math.round(a.e))} pts</span> vs rythme</>;
  };

  // Le panneau est un FRÈRE de <main> : tous deux sont enfants de la rangée
  // flex du shell. Placé DANS le main, il retombe sous le contenu au lieu de se
  // poser à droite.
  const Panneau = (
    <FiltresPanel
      ouvert={ouvert} onToggle={ouvrirPanneau}
      periodes={MOIS_CHOIX} periode={mois} onPeriode={setMois}
      moisAutres={MOIS_AUTRES}
      groupes={sectionsGroupes(feuilles)}
      onToggleGroupe={(g) => f.setFiltre({ societes: basculerGroupe(g, f.selected, data?.societesDispo || dispo) })}
      societesDispo={data?.societesDispo || dispo} societesActives={f.selected}
      onToggleSociete={f.toggleSociete}
      onToutesSocietes={() => f.setFiltre({ societes: '' })}
      onAucuneSociete={() => f.setFiltre({ societes: '__aucune__' })}
      couleurDe={f.couleurDe}
      nbActifs={(mois ? 1 : 0) + (f.societesUrl ? 1 : 0) + nbFiltresFact}
      onReset={() => { setFiltresFact({}); f.setFiltre({ societes: '', mois: '' }); }}
      categoriesDispo={pucesFact.map((p) => ({ code: `${p.c}|${p.v}`, nom: `${p.lib} : ${libFiltreFact(p.c, p.v)}` }))}
      categoriesActives={pucesFact.map((p) => `${p.c}|${p.v}`)}
      onToggleCategorie={(code) => { const [c, ...v] = code.split('|'); basculerFact(c, v.join('|')); }}
      titreCategories="Détail des factures" categoriesApres
    />
  );
  if (erreur) return <><main className="vt-page aj-page"><div className="aj-erreur">{erreur}</div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page aj-page"><div className="aj-vide">Chargement…</div></main>{Panneau}</>;

  // Ceinture : l'écran ne dépend d'aucun champ que le serveur pourrait ne pas
  // renvoyer. Un rapport qui blanchit vaut moins qu'un rapport qui affiche
  // « aucune donnée » — c'est exactement ce qui arrivait en décochant toutes
  // les sociétés.
  const C = data.cumul || { ca: 0, mo: 0, pieces: 0, marge: 0, ors: 0, heures: 0 };
  const O = data.objectif;
  const attendu = data.rythme?.attendu || 0;
  // Sans objectif ni budget (toute la métropole), le repère est le mois
  // précédent : la question « où en est le mois » se pose autant là-bas.
  const cibleRepli = data.repere?.cible || 0;
  const th = C.heures ? C.mo / C.heures : null;

  // Vue « Main d'œuvre » (2026-10-07) : la M.O. du jour, sa part classée en
  // ventes additionnelles (M.O. payante, règle du rapport Ventes
  // additionnelles), et le reste. Auto Antilles seulement (opération absente
  // de l'export Sellens) : sans `moFamilles`, la vue n'est pas proposée.
  const MF = data.moFamilles;
  const vaDe = (i) => (MF ? MF.familles.reduce((a, fa) => a + ((MF.matrice[fa.code] || [])[i] || 0), 0) : 0);
  const G_VA = 'Ventes additionnelles — M.O. payante';
  const colonnesMo = MF ? [
    { c: 'mo', g: 'Main d\u2019œuvre', lib: 'CA M.O. HT', f: (j) => j.mo, fmt: eur, barre: true },
    ...MF.familles.map((fa) => ({ c: `f_${fa.code}`, g: G_VA, lib: fa.libelle, f: (j, i) => (MF.matrice[fa.code] || [])[i], fmt: eur })),
    { c: 'va', g: G_VA, lib: 'Total', f: (j, i) => vaDe(i), fmt: eur, fort: true },
    { c: 'part', g: G_VA, lib: 'Part', sous: 'de la M.O.', f: (j, i) => (j.mo ? (vaDe(i) / j.mo) * 100 : null), fmt: pct0, ratio: true, discret: true,
      total: () => { const mo = J.reduce((a, j) => a + j.mo, 0); return mo ? (J.reduce((a, j, i) => a + vaDe(i), 0) / mo) * 100 : null; } },
    { c: 'autre', g: '', lib: 'Autre M.O.', sous: 'hors familles, garantie, cession', f: (j, i) => j.mo - vaDe(i), fmt: eur, discret: true },
  ] : [];
  // Par atelier : M.O. puis pièces, groupées sous le nom de l'atelier. Sans
  // pièces (export Sellens) ou sur une réponse ancienne : une colonne par atelier.
  const detailAtelier = !!(data.matriceMo && avecPieces);
  const colonnesAtelier = (data.ateliers || []).flatMap((a) => (detailAtelier
    ? [{ c: `${a}|mo`, g: a, lib: 'M.O.', f: (j, i) => (data.matriceMo[a] || [])[i], fmt: eur },
       { c: `${a}|pc`, g: a, lib: 'Pièces', f: (j, i) => (data.matricePieces[a] || [])[i], fmt: eur, discret: true },
       // Sous-traitance : seulement pour l'atelier qui en a sur le mois.
       ...((data.matriceSt?.[a] || []).some((v) => v)
         ? [{ c: `${a}|st`, g: a, lib: 'Sous-trait.', f: (j, i) => (data.matriceSt[a] || [])[i], fmt: eur, discret: true }] : [])]
    : [{ c: a, lib: a, f: (j, i) => ((data.matriceMo || data.matrice)?.[a] || [])[i], fmt: eur }]));
  // Productivité : heures du jour, occupation et productivité du jour, et
  // l'efficience / le rendement en CUMUL depuis le 1er (sur un jour isolé, le
  // facturé, daté de la facture, ne se compare pas au pointé).
  const prodJour = new Map((prod?.parJour || []).map((x) => [x.jour, x]));
  const pj = (j) => prodJour.get(j.jour);
  const PT = prod?.total || {};
  const G_H = 'Heures'; const G_R = 'Ratios du jour'; const G_C = 'Cumul depuis le 1er';
  const colonnesProd = [
    { c: 'p_pres', g: G_H, lib: 'Présence', f: (j) => pj(j)?.presence, fmt: heures, total: () => PT.presence },
    { c: 'p_pt', g: G_H, lib: 'Pointé OR', f: (j) => pj(j)?.pointeOr, fmt: heures, fort: true, total: () => PT.pointeOr },
    { c: 'p_int', g: G_H, lib: 'Interne', sous: 'attente, entretien…', f: (j) => pj(j)?.interne, fmt: heures, discret: true, total: () => PT.interne },
    { c: 'p_fac', g: G_H, lib: 'Facturé', f: (j) => pj(j)?.facture, fmt: heures, total: () => PT.facture },
    { c: 'p_occ', g: G_R, lib: 'Occupation', f: (j) => pj(j)?.occupation, fmt: pctNul, ratio: true, total: () => PT.occupation },
    { c: 'p_prod', g: G_R, lib: 'Productivité', f: (j) => pj(j)?.productivite, fmt: pctNul, ratio: true, fort: true, total: () => PT.productivite },
    { c: 'p_eff', g: G_C, lib: 'Efficience', f: (j) => pj(j)?.cumul?.efficience, fmt: pctNul, ratio: true, total: () => PT.efficience },
    { c: 'p_rend', g: G_C, lib: 'Rendement', f: (j) => pj(j)?.cumul?.rendement, fmt: pctNul, ratio: true, total: () => PT.rendement },
  ];
  const vueEff = (vue === 'mo' && !MF) || (vue === 'prod' && !prodDispo) ? 'ind' : vue;
  const colonnes = vueEff === 'ind' ? IND_V : vueEff === 'mo' ? colonnesMo
    : vueEff === 'prod' ? colonnesProd : colonnesAtelier;
  const totalDe = (col) => {
    if (col.total) return col.total();
    if (vueEff !== 'ind') return J.reduce((a, j, i) => a + (col.f(j, i) || 0), 0);
    if (col.c === 'tx') return C.ca ? (C.marge / C.ca) * 100 : null;
    if (col.c === 'ticket') return C.ors ? C.ca / C.ors : null;
    // OR du mois : chaque OR une fois (un OR facturé sur deux jours compte
    // dans chacun des deux jours, la somme des jours le compterait deux fois).
    if (col.c === 'ors') return C.ors;
    if (col.c === 'th') return th;
    return J.reduce((a, j) => a + (col.f(j) || 0), 0);
  };

  return (
    <>
      <main className="vt-page aj-page">
      <div className="vt-head">
        <div>
          <h1>APV — Le mois au jour le jour</h1>
        </div>
        <div className="vt-asof"><b>{moisLong(data.mois)}</b></div>
      </div>

      {N === 0 ? (
        <div className="aj-vide">
          {data.vide
            ? 'Aucune société sélectionnée — cochez-en au moins une dans les filtres, à droite.'
            : 'Aucune facture atelier sur ce mois.'}
        </div>
      ) : (
        <>
          <div className="vt-kpis" style={{ gridTemplateColumns: `repeat(${avecPieces ? 7 : 4},1fr)` }}>
            <Kpi lab={`${avecPieces ? 'CA atelier' : 'CA main d\u2019œuvre'} — ${N} j facturé${N > 1 ? 's' : ''}`}
              val={eur(C.ca)}
              sub={<Avanc v={C.ca} o={O?.ca} />} />
            {avecPieces && <Kpi lab="dont main d'œuvre" val={eur(C.mo)} sub={<Avanc v={C.mo} o={O?.mo} />} />}
            {avecPieces && <Kpi lab="dont pièces" val={eur(C.pieces)} sub={<Avanc v={C.pieces} o={O?.pieces} />} />}
            {avecPieces && <Kpi lab="dont sous-traitance" val={eur(C.st || 0)}
              sub={<span className="aj-faint">travaux extérieurs, sans heures</span>} />}
            <Kpi lab="Marge" val={eur(C.marge)}
              sub={<>{C.ca ? `${Math.round((C.marge / C.ca) * 100)} % du CA · ` : ''}<Avanc v={C.marge} o={O?.marge} /></>} />
            {/* Un objectif d'heures partiel comparerait le réalisé de cinq
                ateliers à la cible de deux : on se tait plutôt que de mentir. */}
            <Kpi lab="Heures vendues" val={<>{nb(C.heures)} <small>h</small></>}
              sub={O?.heuresCompletes ? <Avanc v={C.heures} o={O.heures} />
                : <span className="aj-faint">objectif d'heures incomplet sur ce périmètre</span>} />
            <Kpi lab="Taux horaire" val={<>{th ? Math.round(th) : '—'} <small>€/h</small></>}
              sub={O?.heuresCompletes ? `objectif ${Math.round(O.mo / O.heures)} €/h`
                : <span className="aj-faint">pas d'objectif comparable</span>} />
          </div>

          {(O || cibleRepli > 0) && (
            <div className="vt-card">
              <div className="vt-card-h"><h2>Où en est le mois</h2>
                <span className="hint">{N} jour{N > 1 ? 's' : ''} facturé{N > 1 ? 's' : ''}{data.joursOuvres ? ` sur ${data.joursOuvres} ouvrés` : ''}
                  {O ? ` · cible ${O.source}` : " · pas d'objectif saisi, repère : le mois précédent"}</span></div>
              <AvancementMois
                realise={C.ca} cible={O ? O.ca : cibleRepli} attendu={attendu > 0 ? attendu : null}
                repere={O ? 'objectif' : 'mois précédent'} article={O ? "de l'" : 'du'} fmt={eur}
                jours={`${N} jour${N > 1 ? 's' : ''} facturé${N > 1 ? 's' : ''}`}
                estime={data.rythme?.source === 'lineaire'} />
              {O && O.source === 'budget' && (
                <p className="aj-avert">
                  Aucun objectif n'est saisi pour ce périmètre : c'est le <b>budget</b> qui sert de cible.
                  Il se règle dans <b>Administration ▸ Objectifs APV</b>.
                </p>
              )}
            </div>
          )}

          {(data.societesDispo || []).length > 1 && (data.societesCumul || []).length > 0 && (
            <div className="vt-card">
              <div className="vt-card-h">
                <h2>Par concession — cumul du mois</h2>
                <div className="st-tools">
                  <SelecteurMaille niveau={maille.niveau} setNiveau={maille.setNiveau} dispo={axes} />
                </div>
              </div>
              <div className="aj-scroll">
                <table className="aj-tbl tbl-dense aj-soc">
                  <thead>
                    <tr>
                      <th className="l">Concession</th>
                      {SOC_V.map((col) => (
                        <th key={col.c} className={col.discret ? 'aj-th-discret' : ''}>{col.lib}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {arbreConcessions.map((s) => {
                      const cible = s.feuille ? s.code : (s.membres || []).join(',');
                      return (
                      <tr key={s.cle}
                        className={`tbl-clic rg-n${s.niveau}${f.societesUrl === cible ? ' actif' : ''}`}
                        onClick={() => f.setFiltre({ societes: basculer(f.societesUrl, cible) })}>
                        <td className="l aj-fort">
                          <span className="rg-in" style={{ width: s.niveau * 14 }} />
                          {/* Le dépliement a son propre bouton : cliquer la LIGNE
                              filtre la page sur le regroupement entier. */}
                          {s.feuille
                            ? <span className="rg-chev vide" />
                            : (
                              <button type="button" className={`rg-btn${maille.ouverts.has(s.cle) ? ' on' : ''}`}
                                title={maille.ouverts.has(s.cle) ? 'Replier' : 'Voir le détail par concession'}
                                aria-label="Déplier"
                                onClick={(e) => { e.stopPropagation(); maille.basculer(s.cle); }}>▶</button>
                            )}
                          {s.libelle || s.societe}
                          {!s.feuille && <span className="rg-nb">{s.nbEnfants}</span>}
                        </td>
                        {SOC_V.map((col) => (
                          <td key={col.c} className={col.discret ? 'aj-discret' : ''}>{col.fmt(col.f(s))}</td>
                        ))}
                      </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="aj-tot">
                      <td className="l">Ensemble</td>
                      {SOC_V.map((col) => (
                        <td key={col.c} className={col.discret ? 'aj-discret' : ''}>{col.fmt(col.f(C))}</td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          <div className="vt-card">
            <div className="vt-card-h">
              <h2>{vueEff === 'ind' ? 'Le mois jour par jour' : vueEff === 'mo' ? 'Main d\u2019œuvre — par jour, avec les ventes additionnelles' : vueEff === 'prod' ? 'Productivité des compagnons — par jour' : (detailAtelier ? 'Par atelier — main d\u2019œuvre et pièces, par jour' : 'Main d\u2019œuvre — par jour et par atelier')}</h2>
              <div className="st-tools">
                {vueEff === 'prod' && <span className="hint" title="Occupation = tout le pointé ÷ présence ; productivité = pointé OR ÷ présence ; efficience = facturé ÷ pointé OR ; rendement = facturé ÷ présence. Source : APV — Productivité.">
                  {!prod ? 'chargement…' : prod.erreur ? 'productivité indisponible' : `efficience et rendement cumulés : le facturé est daté de la facture${PT.nonRapproches ? ` · ${PT.nonRapproches} compagnon${PT.nonRapproches > 1 ? 's' : ''} sans présence Kelio` : ''}`}
                </span>}
                {vueEff !== 'mo' && !avecPieces && <span className="hint">M.O. seule — l'export Sellens ne porte pas les pièces</span>}
                <span className="st-seg">
                  <button type="button" className={vueEff === 'ind' ? 'on' : ''} onClick={() => setVue('ind')}>Indicateurs</button>
                  {MF && <button type="button" className={vueEff === 'mo' ? 'on' : ''} onClick={() => setVue('mo')}>Main d'œuvre</button>}
                  <button type="button" className={vueEff === 'atl' ? 'on' : ''} onClick={() => setVue('atl')}>Par atelier</button>
                  {prodDispo && <button type="button" className={vueEff === 'prod' ? 'on' : ''} onClick={() => setVue('prod')}>Productivité</button>}
                </span>
              </div>
            </div>
            <div className="aj-scroll">
              <table className={`aj-tbl tbl-dense${vueEff === 'mo' ? ' aj-tbl-mo' : ''}`}>
                <thead>
                  {(vueEff !== 'atl' || detailAtelier) && (
                    <tr className="aj-groupes">
                      <th className="l" />
                      {groupes(colonnes).map((g, i) => (
                        <th key={i} colSpan={g.n}>{g.lib}</th>
                      ))}
                    </tr>
                  )}
                  <tr>
                    <th className="l">{N} jours facturés</th>
                    {colonnes.map((col) => (
                      <th key={col.c} className={col.discret ? 'aj-th-discret' : ''}>
                        {col.lib}{col.sous && <small>{col.sous}</small>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {J.map((j, i) => (
                    <tr key={j.jour} className={`${i === iDernier ? 'tbl-courant' : ''}${estWeekend(j.jour) ? ' aj-weekend' : ''}${j.sansFacture ? ' aj-sans' : ' tbl-clic'}${filtresFact.jour.includes(j.jour) ? ' actif' : ''}`}
                      title={j.sansFacture ? undefined : filtresFact.jour.includes(j.jour) ? 'Retirer ce jour du détail des factures' : 'Voir les factures de ce jour'}
                      onClick={j.sansFacture ? undefined : () => basculerFact('jour', j.jour)}>
                      <td className="l aj-jour">
                        <b>{jourCourt(j.jour)}</b>
                        {estWeekend(j.jour) && <span className="aj-we">hors jour ouvré</span>}
                        {i === iDernier && <span className="aj-tag">dernier jour facturé</span>}
                        {j.sansFacture && <span className="aj-tag aj-zero">aucune facture</span>}
                      </td>
                      {colonnes.map((col, ci) => {
                        const v = col.f(j, i);
                        const txt = v == null || (v === 0 && !j.sansFacture) ? '—' : col.fmt(v);
                        // Le CA porte une barre proportionnelle : une seule
                        // grandeur mise en image, celle qui porte la décision.
                        if (col.barre) return <td key={col.c} className="aj-fort">{txt}</td>;
                        return <td key={col.c} className={col.discret ? 'aj-discret' : col.fort ? 'aj-fort' : ''}>{txt}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="aj-tot">
                    <td className="l">Total du mois</td>
                    {colonnes.map((col) => (
                      <td key={col.c} className={col.discret ? 'aj-discret' : ''}>{col.fmt(totalDe(col))}</td>
                    ))}
                  </tr>
                  <tr className="sous">
                    <td className="l">Moyenne par jour</td>
                    {colonnes.map((col) => (
                      <td key={col.c} className={col.discret ? 'aj-discret' : ''}>
                        {col.ratio ? <span className="aj-faint">—</span>
                          : col.fmt(J.reduce((a, j, i) => a + (col.f(j, i) || 0), 0) / (joursOuvresEcoules || 1))}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {data.parAxe && (data.parAxe[axe] || []).length > 0 && (
            <div className="vt-card">
              <div className="vt-card-h">
                <h2>Le mois par {AXE_APV.lib}</h2>
                <div className="st-tools">
                  <div className="st-seg">
                    {AXES_APV.map((a) => {
                      const off = !((data.parAxe || {})[a.c] || []).length;
                      return (
                        <button key={a.c} type="button" disabled={off}
                          className={axe === a.c ? 'on' : ''}
                          title={off ? `« ${a.col} » n'est renseigné sur aucune facture du mois` : ''}
                          onClick={() => setAxe(a.c)}>{a.lib}</button>
                      );
                    })}
                  </div>
                </div>
              </div>
              {/* Quarante-cinq réceptionnaires pousseraient le reste du rapport
                  hors de l'écran : le bloc défile chez lui, en-tête collé. */}
              <div className="aj-scroll vt-borne">
                <table className="aj-tbl tbl-dense">
                  <thead>
                    <tr>
                      <th className="l">{AXE_APV.col}</th>
                      {SOC_AXE.map((col) => (
                        <th key={col.c} className={col.discret ? 'aj-th-discret' : ''}>{col.lib}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(data.parAxe[axe] || []).map((x) => (
                      <tr key={x.nom} className={`tbl-clic${filtresFact[axe]?.includes(x.nom) ? ' actif' : ''}`}
                        title={filtresFact[axe]?.includes(x.nom) ? 'Retirer ce filtre du détail des factures' : 'Voir les factures de cette ligne'}
                        onClick={() => basculerFact(axe, x.nom)}>
                        <td className="l aj-fort">{x.nom}</td>
                        {SOC_AXE.map((col) => (
                          <td key={col.c} className={col.discret ? 'aj-discret' : ''}>{col.fmt(col.f(x))}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="l">Ensemble</td>
                      {SOC_AXE.map((col) => (
                        <td key={col.c} className={col.discret ? 'aj-discret' : ''}>{col.fmt(col.f(C))}</td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* ── Détail des factures (2026-10-27) ── */}
          <div className="vt-card">
            <div className="vt-card-h">
              <h2>Détail des factures</h2>
              <div className="st-tools">
                <span className="hint">
                  {factures === null ? 'chargement…' : factures.erreur ? 'indisponible'
                    : `${new Intl.NumberFormat('fr-FR').format(facturesFiltrees.length)} facture${facturesFiltrees.length > 1 ? 's' : ''}${facturesFiltrees.length > MAX_FACTURES ? ` · ${MAX_FACTURES} premières affichées` : ''} · cliquer un jour ou une ligne des blocs du haut pour filtrer`}
                </span>
              </div>
            </div>
            {pucesFact.length > 0 && (
              <div className="st-sous-h">
                {pucesFact.map((p) => (
                  <span className="st-puce" key={`${p.c}|${p.v}`}>
                    {p.lib} : <b>{libFiltreFact(p.c, p.v)}</b>
                    <button type="button" onClick={() => basculerFact(p.c, p.v)} aria-label="Retirer ce filtre" title="Retirer ce filtre">×</button>
                  </span>
                ))}
                {pucesFact.length > 1 && <button type="button" className="st-lien" onClick={() => setFiltresFact({})}>tout retirer</button>}
              </div>
            )}
            {Array.isArray(factures) && (
              <div className="aj-scroll vt-borne">
                <table className="aj-tbl tbl-dense aj-fact">
                  <thead>
                    <tr>
                      {[['jour', 'Date', 'l'], ['societe', 'Société', 'l'], ['facture', 'N° facture', 'l'], ['or', 'N° OR', 'l'], ['atelier', 'Atelier', 'l'], ['type', 'Type', 'l'], ['marque', 'Marque', 'l'], ['receptionnaire', 'Réceptionnaire', 'l'],
                        ['mo', 'M.O. HT', ''], ['pieces', 'Pièces HT', ''], ['st', 'Sous-trait. HT', ''], ['ca', 'CA HT', ''], ['marge', 'Marge', '']].map(([c, l, cl]) => (
                        <th key={c} className={`${cl} st-triable`} title="Trier" onClick={() => setTriFact((t) => (t.c === c ? { c, desc: !t.desc } : { c, desc: !['societe', 'facture', 'or', 'atelier', 'type', 'marque', 'receptionnaire'].includes(c) }))}>
                          {l}{triFact.c === c && <span className="st-tri-fleche">{triFact.desc ? '▾' : '▴'}</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {facturesTriees.slice(0, MAX_FACTURES).map((fa) => (
                      <tr key={`${fa.societeCode}|${fa.facture}|${fa.or}`}>
                        <td className="l">{new Date(`${fa.jour}T12:00:00`).toLocaleDateString('fr-FR')}</td>
                        <td className="l">{fa.societe}</td>
                        <td className="l aj-mono">{fa.facture}</td>
                        <td className="l aj-mono">{fa.or}</td>
                        <td className="l">{fa.atelier}</td>
                        <td className="l">{fa.type}</td>
                        <td className="l">{fa.marque}</td>
                        <td className="l">{fa.receptionnaire}</td>
                        <td>{eur2(fa.mo)}</td>
                        <td className="aj-discret">{eur2(fa.pieces)}</td>
                        <td className="aj-discret">{fa.st ? eur2(fa.st) : ''}</td>
                        <td className="aj-fort">{eur2(fa.ca)}</td>
                        <td className="aj-discret">{eur2(fa.marge)}</td>
                      </tr>
                    ))}
                    {facturesFiltrees.length === 0 && <tr><td colSpan={13} className="l aj-faint">Aucune facture pour ces filtres.</td></tr>}
                  </tbody>
                  {facturesFiltrees.length > 0 && (
                    <tfoot>
                      <tr className="aj-tot">
                        <td className="l" colSpan={8}>Total des {new Intl.NumberFormat('fr-FR').format(facturesFiltrees.length)} factures{nbFiltresFact ? ' filtrées' : ' du mois'}</td>
                        {['mo', 'pieces', 'st', 'ca', 'marge'].map((k) => <td key={k}>{eur(facturesFiltrees.reduce((a, x) => a + (x[k] || 0), 0))}</td>)}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </div>
        </>
      )}

    </main>
      {Panneau}
    </>
  );
}
